import ContentOperationPlugin from './contentOperation/ContentOperationPlugin';
import DockingPluginBase from './docking/DockingPluginBase';
import ComposeViewState from '../store/schema/ComposeViewState';
import { AddinEditorPlugin } from 'owa-addins-editor-plugin';
import { LazyCortanaComposePickerPlugin } from 'owa-cortana-compose';
import { UndoService } from 'owa-editor';
import { LazyAccCheckerPlugin } from 'owa-editor-acc-checker-plugin/lib/plugin/AccCheckerPlugin';
import { LazyEmojiPickerPlugin } from 'owa-editor-emoji-picker-plugin';
import { LazyEmojiPlugin } from 'owa-editor-emojiplugin/lib/plugin/EmojiPlugin';
import { LazyLinkPlugin } from 'owa-editor-link-plugin';
import { LazyAtMentionsPickerPlugin } from 'owa-editor-mentions-picker-plugin';
import { LazyPasteOptionPlugin } from 'owa-editor-paste-option-plugin';
import RibbonPlugin from 'owa-editor-ribbonplugin/lib/plugin/RibbonPlugin';
import { LazySmartSuggestionsPlugin } from 'owa-editor-smart-suggestions-plugin';
import { LazySmartComposePlugin } from 'owa-editor-smartcompose-plugin';
import { TableEditPlugin } from 'owa-editor-table-edit-plugin';
import { InlineImagePlugin, LazyInlineImageResizePlugin } from 'owa-inline-image';
import { LazyLinkPreviewPlugin } from 'owa-link-preview-compose';
import { EditorPlugin } from 'roosterjs-editor-core';

interface ComposePlugins {
    undo: UndoService;
    dockingPlugin: DockingPluginBase;
    ribbonPlugin: RibbonPlugin;
    inlineImagePlugin: InlineImagePlugin;
    inlineImageResizePlugin: LazyInlineImageResizePlugin;
    emojiPlugin?: LazyEmojiPlugin;
    emojiPickerPlugin?: LazyEmojiPickerPlugin;
    pasteOptionPlugin: LazyPasteOptionPlugin;
    contentOperationPlugin: ContentOperationPlugin;
    smartSuggestionsPlugin?: LazySmartSuggestionsPlugin;
    linkPreviewPlugin?: LazyLinkPreviewPlugin;
    addinPlugin?: AddinEditorPlugin;
    tableEdit: TableEditPlugin;
    accCheckerPlugin?: LazyAccCheckerPlugin;
    linkPlugin?: LazyLinkPlugin;
    mentionsPickerPlugin?: LazyAtMentionsPickerPlugin;
    cortanaComposePlugin?: LazyCortanaComposePickerPlugin;
    smartComposePlugin?: LazySmartComposePlugin;
}

export default ComposePlugins;

const pluginMap: { [key: number]: ComposePlugins } = {};

export function addComposePlugins(viewState: ComposeViewState, plugins: ComposePlugins) {
    pluginMap[viewState.composeId] = plugins;
}

export function findComposePlugins(viewState: ComposeViewState): ComposePlugins {
    return pluginMap[viewState.composeId];
}

export function removeComposePlugins(viewState: ComposeViewState) {
    const plugins = findComposePlugins(viewState);
    if (plugins && plugins.contentOperationPlugin) {
        // We are removing all the plugins, stop auto save since we don't need that now.
        plugins.contentOperationPlugin.stopSaveOnDispose();
    }
    delete pluginMap[viewState.composeId];
}

export function getPluginArray(composePlugins: ComposePlugins): EditorPlugin[] {
    const plugins: EditorPlugin[] = [
        composePlugins.dockingPlugin,
        composePlugins.ribbonPlugin,
        composePlugins.inlineImagePlugin,
        composePlugins.inlineImageResizePlugin,
        composePlugins.contentOperationPlugin,
        composePlugins.tableEdit,
    ];

    // features behind flags
    function tryPush(plugin: EditorPlugin) {
        if (plugin) {
            plugins.push(plugin);
        }
    }

    tryPush(composePlugins.emojiPlugin);
    tryPush(composePlugins.smartSuggestionsPlugin);
    tryPush(composePlugins.linkPreviewPlugin);
    tryPush(composePlugins.addinPlugin);
    tryPush(composePlugins.accCheckerPlugin);
    tryPush(composePlugins.pasteOptionPlugin);
    tryPush(composePlugins.linkPlugin);
    tryPush(composePlugins.mentionsPickerPlugin);
    tryPush(composePlugins.cortanaComposePlugin);
    tryPush(composePlugins.emojiPickerPlugin);
    tryPush(composePlugins.smartComposePlugin);

    return plugins;
}
